import React, { ReactNode, useState } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import DashboardIcon from "@mui/icons-material/Dashboard";
import LogoutIcon from "@mui/icons-material/Logout";
import SchoolIcon from "@mui/icons-material/School";
import VisibilityIcon from "@mui/icons-material/Visibility";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

type TeacherLayoutProps = {
  children: ReactNode;
};

const TeacherLayout: React.FC<TeacherLayoutProps> = ({ children }) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    signOut();
    navigate("/auth/sign-in");
    handleClose();
  };

  const displayName = user?.name || [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.email || "";

  const getUserInitials = () => {
    if (!displayName) return "?";
    const parts = displayName.split(" ");
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return parts[0][0]?.toUpperCase() || "?";
  };

  return (
    <>
      <AppBar
        position="static"
        elevation={2}
        sx={{
          backgroundColor: (theme) => theme.palette.primary.main,
          marginBottom: 3,
        }}
      >
        <Toolbar>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <SchoolIcon sx={{ mr: 2 }} />
            <Typography
              variant="h6"
              component="div"
              sx={{ fontWeight: "bold" }}
            >
              Teacher Dashboard
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* Avatar with dropdown */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              "&:hover": { opacity: 0.9 },
            }}
            onClick={handleProfileClick}
            aria-controls={open ? "profile-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
          >
            <Typography
              variant="body1"
              sx={{
                mr: 1.5,
                display: { xs: "none", sm: "block" },
              }}
            >
              {displayName}
            </Typography>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: (theme) => theme.palette.secondary.main,
                color: (theme) => theme.palette.secondary.contrastText,
                cursor: "pointer",
              }}
            >
              {getUserInitials()}
            </Avatar>
          </Box>

          {/* Profile Menu */}
          <Menu
            id="profile-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            PaperProps={{
              elevation: 3,
              sx: {
                width: 250,
                mt: 1.5,
                overflow: "visible",
                filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.1))",
                "&:before": {
                  content: '""',
                  display: "block",
                  position: "absolute",
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: "background.paper",
                  transform: "translateY(-50%) rotate(45deg)",
                  zIndex: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            {/* User info section */}
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                {displayName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.email}
              </Typography>
            </Box>

            <Divider />

            <MenuItem onClick={() => navigate("/teacher/profile")}>
              <ListItemIcon>
                <AccountCircleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>My Profile</ListItemText>
            </MenuItem>

            <MenuItem onClick={() => navigate("/teacher/dashboard")}>
              <ListItemIcon>
                <DashboardIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Dashboard</ListItemText>
            </MenuItem>

            <MenuItem onClick={() => navigate("/teacher/classrooms")}>
              <ListItemIcon>
                <SchoolIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>My Classrooms</ListItemText>
            </MenuItem>

            <MenuItem onClick={() => navigate("/teacher/preview")}>
              <ListItemIcon>
                <VisibilityIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Content Preview</ListItemText>
            </MenuItem>

            <MenuItem onClick={() => navigate("/teacher/guide")}>
              <ListItemIcon>
                <HelpOutlineIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Teacher Guide</ListItemText>
            </MenuItem>

            <Divider />

            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText sx={{ color: theme.palette.error.main }}>
                Sign Out
              </ListItemText>
            </MenuItem>

            {process.env.REACT_APP_VERSION && (
              <>
                <Divider />
                <Box sx={{ px: 2, py: 0.5 }}>
                  <Typography variant="caption" color="text.disabled">
                    v{process.env.REACT_APP_VERSION}
                    {process.env.REACT_APP_BUILD_HASH
                      ? ` (${process.env.REACT_APP_BUILD_HASH})`
                      : ""}
                  </Typography>
                </Box>
              </>
            )}
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="main"
        sx={{
          padding: 2,
          maxWidth: 1200,
          margin: "auto",
        }}
      >
        {children}
      </Box>
    </>
  );
};

export default TeacherLayout;
